import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { campaignAPI, socialAPI, referralAPI } from "@/services/api";
import { REFERRAL_ORIGIN } from "@/config/api";
import { motion } from "framer-motion";
import {
  Search, SlidersHorizontal, ChevronRight, ChevronLeft,
  Users, TrendingUp, Zap, Copy, CheckCircle2, Link2,
  ArrowRight, Megaphone
} from "lucide-react";
import {
  TailAdminEmptyState,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";
import CampaignTypeBadge from "@/components/campaign/CampaignTypeBadge";

const GOAL_LABELS = {
  subscription: "Subscription",
  registration: "Registration",
  download: "Download",
  purchase: "Purchase",
};

const GOAL_COLORS = {
  subscription: "bg-violet-100 text-violet-700",
  registration: "bg-brand-50 text-brand-500",
  download: "bg-teal-100 text-teal-700",
  purchase: "bg-emerald-100 text-emerald-700",
};
const goalLabel = (campaign) => campaign.goal_actionable === false ? `${GOAL_LABELS[campaign.goal_type] || campaign.goal_type} · Coming later` : (GOAL_LABELS[campaign.goal_type] || campaign.goal_type);

const BRMCampaigns = () => {
  const navigate = useNavigate();

  // ── Marketplace state ───────────────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null); // campaignId being joined

  // ── My active links state ───────────────────────────────────────────────────
  const [myLinks, setMyLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [myNiches, setMyNiches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [requestType, setRequestType] = useState("");
  const [directVersion, setDirectVersion] = useState("");
  const [audiencePay, setAudiencePay] = useState("");
  const [payoutType, setPayoutType] = useState("");
  const [category, setCategory] = useState("");
  const [geography, setGeography] = useState("");
  const [productType, setProductType] = useState("");

  // ── Tabs ────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("marketplace"); // marketplace | my-links

  // ── Load marketplace ────────────────────────────────────────────────────────
  const loadCampaigns = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const res = await campaignAPI.listCampaigns({
        page: currentPage,
        pageSize: 12,
        request_type: requestType || undefined,
        direct_version: directVersion || undefined,
        audience_pay: audiencePay === "yes" ? true : audiencePay === "no" ? false : undefined,
        payout_type: payoutType || undefined,
        category: category || undefined,
        geography: geography || undefined,
        product_type: productType || undefined,
        q: searchQuery.trim() || undefined,
      });
      setCampaigns(res.campaigns || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [audiencePay, category, directVersion, geography, payoutType, productType, requestType, searchQuery]);

  // ── Load BRM's active campaign links ────────────────────────────────────────
  const loadMyLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const res = await referralAPI.getMyLinks();
      setMyLinks(res.data || []);
    } catch {
      // Non-fatal
    } finally {
      setLinksLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      loadMyLinks(),
      socialAPI.getMyNiches().then(r => setMyNiches(r.niches || [])).catch(() => {}),
    ]);
  }, [loadMyLinks]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadCampaigns(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [loadCampaigns]);

  // ── Join campaign ────────────────────────────────────────────────────────────
  const handleJoin = async (campaignId, e) => {
    e.stopPropagation();
    setJoining(campaignId);
    try {
      const result = await campaignAPI.joinCampaign(campaignId);
      const participation = result?.data || result;
      toast.success(
        participation?.status === "pending_negotiation"
          ? "Application saved. Agree on terms before promotion starts."
          : "Joined! Your deep-link is ready."
      );
      await Promise.all([loadCampaigns(page), loadMyLinks()]);
      // Navigate to detail to show the link prominently
      navigate(`/brm/campaigns/${campaignId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join campaign");
    } finally {
      setJoining(null);
    }
  };

  // ── Copy deep-link ────────────────────────────────────────────────────────────
  const copyLink = (token, e) => {
    e?.stopPropagation();
    const url = `${REFERRAL_ORIGIN}/r/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    toast.success("Deep-link copied! Share it to start earning.");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  // ── Derived filtered list ────────────────────────────────────────────────────
  const joinedIds = new Set(myLinks.map(l => l.campaign_id));

  // The server owns eligibility, filtering and pagination. Client state never
  // hides rows from a page or implies eligibility from a cosmetic niche match.
  const filtered = campaigns;

  const activeLinks = myLinks.filter(l => l.campaign_status === "active");

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="px-4 py-6 space-y-5 pb-24">

      <TailAdminPageHeader
        eyebrow="Campaigns"
        title="Opportunity Market"
        description="Compare verified campaign opportunities, understand the exact proof request, and take the next permitted action."
      />

      {/* Tab switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-theme-xs">
        <button
          onClick={() => setActiveTab("marketplace")}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all ${
            activeTab === "marketplace"
              ? "bg-brand-500 text-white shadow-theme-xs"
              : "text-gray-500"
          }`}
        >
          Marketplace
        </button>
        <button
          onClick={() => setActiveTab("my-links")}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all relative ${
            activeTab === "my-links"
              ? "bg-brand-500 text-white shadow-theme-xs"
              : "text-gray-500"
          }`}
        >
          My Links
          {activeLinks.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-brand-50 text-brand-500 rounded-full">
              {activeLinks.length}
            </span>
          )}
        </button>
      </div>

      {/* ── TAB: My Active Campaign Links ─────────────────────────────────────── */}
      {activeTab === "my-links" && (
        <div className="space-y-4">
          {/* Explanation banner - clearly distinguishes from BRM referral code */}
          <TailAdminPanel title="Campaign Deep-Links" description="Your campaign links are different from your BRM invite code." action={<Link2 className="h-5 w-5 text-brand-500" />}>
            <div className="flex gap-3 items-start">
              <Link2 className="w-5 h-5 text-brand-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">These are your campaign deep-links</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Each link is unique to you + this campaign. When someone clicks it and
                  converts within 30 days, you earn the commission - even if they didn't
                  convert immediately. Share on IG, TikTok, WhatsApp, anywhere.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  <span className="text-amber-600 font-medium">Not these links:</span> Your BRM
                  invite code (for recruiting other agents) is on the <strong>Referrals</strong> page.
                  These are different - these earn you money from product conversions.
                </p>
              </div>
            </div>
          </TailAdminPanel>

          {linksLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-2xl" />
            ))
          ) : activeLinks.length === 0 ? (
            <TailAdminEmptyState
              icon={Link2}
              title="No active campaign links yet"
              description="Join a campaign from the Marketplace tab to get your first deep-link."
              action={<Button variant="outline" size="sm" onClick={() => setActiveTab("marketplace")}>Browse campaigns</Button>}
            />
          ) : (
            activeLinks.map((link) => (
              <motion.div
                key={link.token}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <section
                  className="cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs transition-shadow hover:shadow-theme-sm"
                  onClick={() => navigate(`/brm/campaigns/${link.campaign_id}`)}
                >
                  <div className="space-y-3">
                    {/* Campaign name + commission */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{link.campaign_title}</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full mt-1 inline-block ${GOAL_COLORS[link.goal_type] || "bg-gray-100 text-gray-500"}`}>
                          {GOAL_LABELS[link.goal_type] || link.goal_type}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-black text-emerald-600">
                          ₦{link.commission_per_goal?.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-gray-500">per conversion</p>
                      </div>
                    </div>

                    {/* Deep-link display */}
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5">
                      <p className="flex-1 text-xs font-mono text-gray-500 truncate">
                        /r/{link.token}
                      </p>
                      <Button
                        size="sm"
                        variant={copiedToken === link.token ? "default" : "secondary"}
                        className="h-7 px-3 rounded-md text-xs shrink-0"
                        onClick={(e) => copyLink(link.token, e)}
                      >
                        {copiedToken === link.token
                          ? <><CheckCircle2 className="w-3 h-3 mr-1" />Copied</>
                          : <><Copy className="w-3 h-3 mr-1" />Copy</>
                        }
                      </Button>
                    </div>

                    {/* Attribution funnel stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {[
                        { label: "Clicks", value: link.clicks, color: "text-brand-500" },
                        { label: "Conversions", value: link.conversions, color: "text-emerald-600" },
                        { label: "Earned", value: `₦${link.revenue?.toLocaleString() || 0}`, color: "text-brand-500" },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="bg-gray-50 rounded-lg p-2">
                          <p className={`text-sm font-black ${color}`}>{value}</p>
                          <p className="text-[10px] text-gray-500">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </motion.div>
            ))
          )}

          {/* Ended/past links */}
          {myLinks.filter(l => l.campaign_status !== "active").length > 0 && (
            <div>
              <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">
                Past campaigns
              </p>
              {myLinks.filter(l => l.campaign_status !== "active").map((link) => (
                <section key={link.token} className="mb-2 flex items-center justify-between rounded-xl border border-gray-200 bg-white p-3 opacity-60">
                    <p className="text-sm font-medium">{link.campaign_title}</p>
                    <TailAdminStatusBadge tone="neutral"><span className="capitalize">{link.campaign_status}</span></TailAdminStatusBadge>
                </section>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Marketplace ──────────────────────────────────────────────────── */}
      {activeTab === "marketplace" && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-500/50 transition-colors"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <details className="rounded-xl border border-gray-200 bg-white p-3 shadow-theme-xs">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200">
              <SlidersHorizontal className="h-4 w-4 text-brand-500" aria-hidden="true" />
              Filter opportunities
            </summary>
            <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
              <p className="col-span-2 text-xs leading-relaxed text-gray-500 md:col-span-4">
                Direct requests support assisted enrollment; Broadcast requests use shareable attribution links. Direct V1 uses the established enrollment flow, while V2 adds a Client-defined registration form. Subscription is the only actionable goal in this release; other goal types are clearly marked Coming later.
              </p>
              {[
                ["Request type", requestType, setRequestType, [["", "All"], ["direct", "Direct"], ["broadcast", "Broadcast"], ["legacy", "Legacy"]]],
                ["Direct version", directVersion, setDirectVersion, [["", "All"], ["v1", "V1"], ["v2", "V2"]]],
                ["Audience pay", audiencePay, setAudiencePay, [["", "All"], ["yes", "Available"], ["no", "Not included"]]],
                ["Payout type", payoutType, setPayoutType, [["", "All"], ["performance", "Performance"], ["media_fee", "Conditional media fee"]]],
              ].map(([label, value, setter, options]) => (
                <label key={label} className="text-xs font-medium text-gray-600">
                  {label}
                  <select
                    value={value}
                    onChange={(event) => setter(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm text-gray-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  >
                    {options.map(([optionValue, optionLabel]) => (
                      <option key={optionValue || "all"} value={optionValue}>{optionLabel}</option>
                    ))}
                  </select>
                </label>
              ))}
              {[
                ["Category", category, setCategory, "e.g. Fintech"],
                ["Geography", geography, setGeography, "e.g. Nigeria"],
                ["Product type", productType, setProductType, "e.g. SaaS"],
              ].map(([label, value, setter, placeholder]) => (
                <label key={label} className="text-xs font-medium text-gray-600">
                  {label}
                  <input
                    value={value}
                    onChange={(event) => setter(event.target.value)}
                    placeholder={placeholder}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  />
                </label>
              ))}
              <button
                type="button"
                onClick={() => {
                  setRequestType(""); setDirectVersion(""); setAudiencePay("");
                  setPayoutType(""); setCategory(""); setGeography(""); setProductType("");
                }}
                className="self-end rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:border-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-200"
              >
                Clear filters
              </button>
            </div>
          </details>

          <p className="text-xs text-gray-500">{total} server-filtered opportunities · Eligibility and next actions are calculated by Proovam.</p>

          {/* My niches if any */}
          {myNiches.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              <p className="text-xs text-gray-500 w-full">Your niches:</p>
              {myNiches.map(n => (
                <TailAdminStatusBadge key={n} tone="neutral">{n}</TailAdminStatusBadge>
              ))}
              <Button variant="ghost" size="sm" className="h-5 px-1 text-xs text-brand-500" onClick={() => navigate("/brm/social")}>
                Edit
              </Button>
            </div>
          )}

          {/* Campaign cards */}
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-44 w-full rounded-2xl" />
            ))
          ) : filtered.length === 0 ? (
            <TailAdminEmptyState
              icon={Megaphone}
              title="No campaigns found"
            />
          ) : (
            <div className="space-y-3">
              {filtered.map((campaign) => {
                const isJoined = campaign.has_joined || joinedIds.has(campaign.campaign_id);
                const negotiationPending = campaign.participation_status === "pending_negotiation";
                const myLink = myLinks.find(l => l.campaign_id === campaign.campaign_id);

                return (
                  <motion.div
                    key={campaign.campaign_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <section
                      className="cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs transition-shadow hover:shadow-theme-sm"
                      onClick={() => navigate(`/brm/campaigns/${campaign.campaign_id}`)}
                    >
                      <div className="space-y-3">
                        {/* Title row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <CampaignTypeBadge requestType={campaign.request_type} directVersion={campaign.direct_version} />
                              {campaign.audience_pay_enabled && (
                                <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                  Audience Pay Available
                                </span>
                              )}
                              {isJoined && (
                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                  {negotiationPending ? "Negotiation pending" : <><CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Joined</>}
                                </span>
                              )}
                              {!isJoined && campaign.eligible && (
                                <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-500">
                                  <Zap className="w-2.5 h-2.5 mr-0.5" />Eligible
                                </span>
                              )}
                            </div>
                            <p className="font-bold text-sm leading-snug">{campaign.title}</p>
                            {campaign.description && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                {campaign.description}
                              </p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-500 shrink-0 mt-1" />
                        </div>

                        {/* Commission + goal */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-emerald-50 rounded-lg p-2 text-center">
                            <p className="text-base font-black text-emerald-700">
                              ₦{campaign.commission_per_goal?.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-emerald-600">per conversion</p>
                          </div>
                          <div className={`px-3 py-2 rounded-lg text-xs font-semibold ${GOAL_COLORS[campaign.goal_type] || "bg-gray-100 text-gray-500"}`}>
                            {goalLabel(campaign)}
                          </div>
                        </div>

                        {/* Niches */}
                        {campaign.niches?.length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {campaign.niches.slice(0, 4).map(n => (
                              <span
                                key={n}
                                className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                  myNiches.includes(n)
                                    ? "border-brand-300 bg-brand-50 text-brand-500"
                                    : "border-gray-200 text-gray-500"
                                }`}
                              >
                                {n}
                              </span>
                            ))}
                            {campaign.niches.length > 4 && (
                              <span className="text-[10px] text-gray-500 px-1">
                                +{campaign.niches.length - 4}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Users className="w-3 h-3" />
                            <span>{campaign.participation_count} BRMs promoting</span>
                          </div>
                          {campaign.mismatch_reason && <span className="text-[10px] text-amber-700">{campaign.mismatch_reason}</span>}

                          {isJoined && !negotiationPending && myLink?.token ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs rounded-lg"
                              onClick={(e) => { e.stopPropagation(); copyLink(myLink?.token, e); }}
                            >
                              {copiedToken === myLink?.token
                                ? <><CheckCircle2 className="w-3 h-3 mr-1" />Copied</>
                                : <><Copy className="w-3 h-3 mr-1" />Copy Link</>
                              }
                            </Button>
                          ) : isJoined && !negotiationPending ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs rounded-lg"
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/brm/campaigns/${campaign.campaign_id}/hub`);
                              }}
                            >
                              Open command center
                            </Button>
                          ) : negotiationPending ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 text-xs rounded-lg"
                              onClick={(event) => {
                                event.stopPropagation();
                                navigate(`/brm/campaigns/${campaign.campaign_id}`);
                              }}
                            >
                              Continue negotiation
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="h-8 text-xs rounded-lg"
                              disabled={joining === campaign.campaign_id || campaign.next_action?.enabled === false}
                              onClick={(e) => handleJoin(campaign.campaign_id, e)}
                            >
                              {joining === campaign.campaign_id
                                ? "Saving..."
                                : campaign.next_action?.label || (campaign.audience_pay_enabled ? "Negotiate" : "Join campaign")}
                              {joining !== campaign.campaign_id && <ArrowRight className="w-3 h-3 ml-1" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    </section>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || loading}
                onClick={() => { setPage(p => p - 1); loadCampaigns(page - 1); }}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages || loading}
                onClick={() => { setPage(p => p + 1); loadCampaigns(page + 1); }}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BRMCampaigns;
